/**
 * The bounded buffer between the socket and the 250 ms flush (FE-12).
 *
 * A background tab keeps receiving frames while its timers are throttled, and
 * the old unbounded buffer then delivered one enormous batch on refocus. Three
 * rules keep it small without losing state:
 *
 * - Frames that carry a channel's whole current state are CONFLATED: only the
 *   newest per key is kept, moved to the position of its latest arrival (so
 *   ordering relative to other frames stays causal). Those are tickers, index,
 *   mark price, order book snapshots, and candle updates for the same bar
 *   (each is the whole bucket as of that fill).
 * - Frames of private account channels and notifications are NEVER dropped
 *   (M2): an order, position or notification event lost here has no resync
 *   path of its own. They are low volume, so they sit outside both caps.
 * - Every other frame (public deltas: trades, order book events, ...) is kept
 *   in order, capped per channel and overall; past a cap the OLDEST frames go,
 *   and the drain names the channels that lost frames so their owners can
 *   refetch from REST.
 */
import { isPrivateChannel } from "../../contexts/WebsocketAuthContext/subscriptionManager";

export const MAX_FRAMES_PER_CHANNEL = 200;
export const MAX_BUFFERED_FRAMES = 2000;

const STATE_CHANNELS = new Set(["ticker", "ticker-frontend", "index", "markprice"]);

const channelOf = (frame: unknown): string | undefined => {
  const record = frame && typeof frame === "object" ? (frame as any) : undefined;
  return typeof record?.channel === "string" ? record.channel : undefined;
};

/** The key under which a frame replaces an older one, or `undefined` to keep every copy. */
export const conflationKey = (frame: unknown): string | undefined => {
  const channel = channelOf(frame);
  if (!channel) return undefined;
  const record = frame as any;
  const prefix = channel.split(":")[0].toLowerCase();
  if (STATE_CHANNELS.has(prefix)) return channel;
  if (prefix === "orderbook" && record.data?.type === "snapshot") {
    return `${channel}#snapshot`;
  }
  if (prefix === "candles" && typeof record.data?.time === "number") {
    return `${channel}#${record.data.time}`;
  }
  return undefined;
};

/** True for frames no cap may drop: private account channels and notifications. */
export const isUndroppableFrame = (frame: unknown) => {
  const channel = channelOf(frame);
  return channel !== undefined && isPrivateChannel(channel.toLowerCase());
};

export interface FrameBufferDrain<T> {
  items: T[];
  /** Frames replaced by a newer frame for the same state. */
  conflated: number;
  /** Delta frames discarded by a cap. */
  dropped: number;
  /** Channels that lost at least one delta frame, first loss first. */
  droppedChannels: string[];
}

export interface FrameBuffer<T> {
  push: (item: T) => void;
  drain: () => FrameBufferDrain<T>;
  readonly size: number;
}

export const createFrameBuffer = <T>(
  frameOf: (item: T) => unknown,
  {
    maxPerChannel = MAX_FRAMES_PER_CHANNEL,
    maxTotal = MAX_BUFFERED_FRAMES,
  }: { maxPerChannel?: number; maxTotal?: number } = {}
): FrameBuffer<T> => {
  let entries = new Map<string, T>();
  // Delta entry ids per channel, oldest first, and the reverse lookup.
  let channelIds = new Map<string, string[]>();
  let channelOfId = new Map<string, string>();
  // Ids of frames outside the caps.
  let exemptIds = new Set<string>();
  let droppedChannels = new Set<string>();
  let seq = 0;
  let conflated = 0;
  let dropped = 0;

  // Only ever called with an id that is still tracked.
  const forgetDelta = (id: string) => {
    const channel = channelOfId.get(id) as string;
    channelOfId.delete(id);
    const ids = channelIds.get(channel) as string[];
    ids.splice(ids.indexOf(id), 1);
    if (!ids.length) channelIds.delete(channel);
    return channel;
  };

  const dropDelta = (id: string) => {
    droppedChannels.add(forgetDelta(id));
    entries.delete(id);
    dropped += 1;
  };

  const nextId = () => {
    const id = `#${seq}`;
    seq += 1;
    return id;
  };

  const push = (item: T) => {
    const frame = frameOf(item);
    const key = conflationKey(frame);
    if (key !== undefined) {
      if (entries.delete(key)) conflated += 1;
      entries.set(key, item);
    } else if (isUndroppableFrame(frame)) {
      const id = nextId();
      entries.set(id, item);
      exemptIds.add(id);
      return;
    } else {
      const channel = channelOf(frame) ?? "";
      const id = nextId();
      entries.set(id, item);
      channelOfId.set(id, channel);
      const ids = channelIds.get(channel) || [];
      ids.push(id);
      channelIds.set(channel, ids);
      if (ids.length > maxPerChannel) dropDelta(ids[0]);
    }

    while (entries.size - exemptIds.size > maxTotal) {
      let oldestKey: string | undefined;
      // eslint-disable-next-line no-restricted-syntax
      for (const candidate of entries.keys()) {
        if (!exemptIds.has(candidate)) {
          oldestKey = candidate;
          break;
        }
      }
      // More non-exempt entries than the cap, so one exists.
      const evict = oldestKey as string;
      if (channelOfId.has(evict)) {
        dropDelta(evict);
      } else {
        entries.delete(evict);
        conflated += 1;
      }
    }
  };

  const drain = (): FrameBufferDrain<T> => {
    const result = {
      items: Array.from(entries.values()),
      conflated,
      dropped,
      droppedChannels: Array.from(droppedChannels),
    };
    entries = new Map();
    channelIds = new Map();
    channelOfId = new Map();
    exemptIds = new Set();
    droppedChannels = new Set();
    conflated = 0;
    dropped = 0;
    return result;
  };

  return {
    push,
    drain,
    get size() {
      return entries.size;
    },
  };
};
